import React, { useState, useEffect } from 'react';
import { compressFiles, optimizeFormData, validateFormData } from '../../../shared/utils/performanceUtils';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaSave, FaArrowLeft, FaUser, FaImage } from 'react-icons/fa';
import { updateAdmin, clearState, getUserProfile } from '../../../redux/Slices/authSlice';
import { useTitle } from '../../../context/TitleContext';
import { useTranslation } from 'react-i18next';
import { showSuccessToast, showErrorToast, showWarningToast, showInfoToast } from '../../../shared/utils/toastHelper';

import { Link, useNavigate, useParams } from 'react-router-dom';

const EditAdmin = () => {
  const { t } = useTranslation('global');
  const { setTitle } = useTitle();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { profile, loading, error, success } = useSelector((state) => state.auth);

  const [adminData, setAdminData] = useState({
    name: '',
    email: '',
    phone: '',
    image: null
  });

  useEffect(() => {
    setTitle('تعديل المشرف');
    dispatch(clearState()); // تنظيف الـ success state القديم
    
    // تحميل بيانات المشرف للتعديل
    // يمكن إضافة API call خاص بجلب بيانات مشرف محدد
    // أو استخدام البيانات المحفوظة في sessionStorage
    const adminDataFromStorage = sessionStorage.getItem(`admin_${id}`);
    if (adminDataFromStorage) {
      try {
        const parsed = JSON.parse(adminDataFromStorage);
        setAdminData({
          name: parsed.name || '',
          email: parsed.email || '',
          phone: parsed.phone || '',
          image: null
        });
      } catch (error) {
      }
    }
  }, [dispatch, setTitle, id]);

  useEffect(() => {
    if (success) {
      showSuccessToast(success);
      dispatch(clearState());
      navigate('/admins'); // العودة لصفحة المشرفين
    }
    if (error) {
      showErrorToast(error);
    }
  }, [success, error, dispatch, navigate]);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setAdminData(prev => ({ 
      ...prev, 
      [name]: files ? files[0] : value 
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // تنظيف أي رسائل قديمة قبل الإرسال
    dispatch(clearState());
    
    if (!adminData.name?.trim()) {
      showErrorToast('يرجى إدخال اسم المشرف');
      return;
    }
    
    if (!adminData.email?.trim()) {
      showErrorToast('يرجى إدخال البريد الإلكتروني');
      return;
    }

    // إنشاء FormData لإرسال البيانات مع الصورة
    const formData = new FormData();
    formData.append('name', adminData.name);
    formData.append('email', adminData.email);
    if (adminData.phone) formData.append('phone', adminData.phone);
    if (adminData.image) formData.append('image', adminData.image);

    dispatch(updateAdmin({ adminId: id, adminData: formData }));
  };

  const LoadingSpinner = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4">
      
      
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/admins" className="text-decoration-none mx-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-outline-secondary btn-sm"
                style={{ borderRadius: '8px' }}
              >
                <FaArrowLeft className="mx-2" />
                العودة
              </motion.button>
            </Link>
            <h4 className="mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.1rem' }}>
              <FaUser className="mx-2" />
              تعديل المشرف
            </h4>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="card shadow-lg border-0"
            style={{ borderRadius: '15px' }}
          >
            <div className="card-header bg-white border-0" style={{ padding: '2rem' }}>
              <h4 className="text-center mb-0 fw-bold" style={{ color: '#184194', fontSize: '1.3rem' }}>
                تعديل بيانات المشرف
              </h4>
            </div>

            <div className="card-body" style={{ padding: '10px 30px' }}>
              <form onSubmit={handleSubmit}>
                {/* Name and Email */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">اسم المشرف</label>
                    <input
                      type="text"
                      name="name"
                      value={adminData.name}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل اسم المشرف"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">البريد الإلكتروني</label>
                    <input
                      type="email"
                      name="email"
                      value={adminData.email}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل البريد الإلكتروني"
                      required
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                </div>

                {/* Phone and Image */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-medium">رقم الهاتف</label>
                    <input
                      type="tel"
                      name="phone"
                      value={adminData.phone}
                      onChange={handleInputChange}
                      className="form-control"
                      placeholder="أدخل رقم الهاتف"
                      style={{ borderRadius: '10px' }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-medium">صورة المشرف</label>
                    <input
                      type="file"
                      name="image"
                      onChange={handleInputChange}
                      className="form-control"
                      accept="image/*"
                      style={{ borderRadius: '10px' }}
                    />
                    <small className="text-muted">
                      <FaImage className="mx-1" />
                      يمكنك اختيار صورة جديدة للمشرف (اختياري)
                    </small>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="text-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    type="submit"
                    className="btn btn-primary px-5 py-3"
                    disabled={loading}
                    style={{ 
                      borderRadius: '12px',
                      fontSize: '1.1rem',
                      fontWeight: '600',
                      backgroundColor: '#184194',
                      border: 'none'
                    }}
                  >
                    {loading ? (
                      <>
                        <div className="spinner-border spinner-border-sm mx-2" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        جاري التحديث...
                      </>
                    ) : (
                      <>
                        <FaSave className="mx-2" />
                        تحديث المشرف
                      </>
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EditAdmin;

